import Hls from 'hls.js';

export class IVSViewer {
  private hls: Hls | null = null;
  private streamId: string;
  private userId: string;
  private playbackUrl: string | null = null;

  constructor(streamId: string, userId: string) {
    this.streamId = streamId;
    this.userId = userId;
  }

  async initialize(videoElement: HTMLVideoElement) {
    try {
      console.log('Fetching AWS IVS playback URL...');
      const { supabase } = await import('@/integrations/supabase/client');
      
      // Get playback URL from backend
      const { data: playbackData, error: playbackError } = await supabase.functions.invoke('get-ivs-playback', {
        body: {
          streamId: this.streamId,
        }
      });

      if (playbackError || !playbackData?.playbackUrl) {
        throw new Error(`Failed to get playback URL: ${playbackError?.message || 'No URL received'}`);
      }

      this.playbackUrl = playbackData.playbackUrl;

      console.log('Playback URL received, initializing HLS player for IVS stream...');

      // Check if HLS.js is supported
      if (Hls.isSupported()) {
        this.hls = new Hls({
          enableWorker: true,
          lowLatencyMode: true,
        });
        
        this.hls.loadSource(this.playbackUrl);
        this.hls.attachMedia(videoElement);
        
        this.hls.on(Hls.Events.MANIFEST_PARSED, () => {
          console.log('IVS stream manifest parsed, starting playback');
          videoElement.play().catch(e => console.warn('Autoplay may be blocked', e));
        });

        this.hls.on(Hls.Events.ERROR, (event, data) => {
          console.error('HLS error:', data);
          if (data.fatal) {
            switch (data.type) {
              case Hls.ErrorTypes.NETWORK_ERROR:
                console.log('Network error, trying to recover...');
                this.hls?.startLoad();
                break;
              case Hls.ErrorTypes.MEDIA_ERROR:
                console.log('Media error, trying to recover...');
                this.hls?.recoverMediaError();
                break;
              default:
                console.error('Fatal error, cannot recover');
                this.hls?.destroy();
                break;
            }
          }
        });
      } else if (videoElement.canPlayType('application/vnd.apple.mpegurl')) {
        // Native HLS support (Safari)
        videoElement.src = this.playbackUrl;
        videoElement.addEventListener('loadedmetadata', () => {
          videoElement.play().catch(e => console.warn('Autoplay may be blocked', e));
        });
      } else {
        throw new Error('HLS is not supported in this browser');
      }

      console.log('IVS viewer initialized successfully');
    } catch (error) {
      console.error('Failed to initialize IVS viewer:', error);
      throw error;
    }
  }

  async disconnect() {
    if (this.hls) {
      this.hls.destroy();
      this.hls = null;
    }
    console.log('IVS viewer disconnected');
  }

  getPlayer(): Hls | null {
    return this.hls;
  }

  getState(): string {
    return this.hls ? 'Playing' : 'Idle';
  }
}
